import { useEffect, useMemo, useState } from 'react';
import { Download, Clipboard, X, Info, Package } from 'lucide-react';
import JSZip from 'jszip';


interface ExportTradingViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// Store contract data for filtering
interface ContractData {
  symbol: string;
  apiAllowed: boolean;
}

export function ExportTradingViewDialog({ isOpen, onClose }: ExportTradingViewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allPairs, setAllPairs] = useState<string[]>([]); // All USDT pairs
  const [contractsData, setContractsData] = useState<ContractData[]>([]); // Contract data for filtering
  const [isPerpFormat, setIsPerpFormat] = useState(false); // OFF by default: exports MEXC:SYMBOLUSDT
  const [splitMode, setSplitMode] = useState(false); // Split into multiple files
  const [apiTradeableOnly, setApiTradeableOnly] = useState(false); // Filter to API-tradeable pairs only
  const PAIRS_PER_FILE = 700;

  // Compute filtered tickers based on apiTradeableOnly toggle
  const tickers = useMemo(() => {
    if (!apiTradeableOnly) {
      return allPairs;
    }
    // Filter to only pairs where apiAllowed === true
    const apiAllowedSymbols = new Set(
      contractsData
        .filter(c => c.apiAllowed === true)
        .map(c => c.symbol.replace('_', ''))
    );
    return allPairs.filter(pair => apiAllowedSymbols.has(pair));
  }, [allPairs, contractsData, apiTradeableOnly]);

  // Count of API-tradeable pairs for display
  const apiTradeableCount = useMemo(() => {
    return contractsData.filter(c => c.apiAllowed === true).length;
  }, [contractsData]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    const fetchSymbols = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch contract details and tickers in parallel (via backend API routes)
        const [contractRes, tickerRes] = await Promise.all([
          fetch('/api/mexc/contract'),
          fetch('/api/mexc/ticker'),
        ]);
        if (!contractRes.ok) throw new Error(`Contract detail HTTP ${contractRes.status}`);
        if (!tickerRes.ok) throw new Error(`Ticker HTTP ${tickerRes.status}`);

        const contractJson = await contractRes.json();
        const tickerJson = await tickerRes.json();

        const contracts: Array<{ symbol: string; quoteCoin: string; state?: number; apiAllowed?: boolean }> = Array.isArray(contractJson?.data)
          ? contractJson.data
          : [];
        const tickers: Array<{ symbol: string }> = Array.isArray(tickerJson?.data) ? tickerJson.data : [];

        // Debug logging (minimal)
        console.log('📊 TradingView Export - Data fetched:', {
          totalContracts: contracts.length,
          totalTickers: tickers.length
        });

        // Build sets for fast lookup - filter tickers to USDT pairs only
        const tickerUsdtSet = new Set(
          tickers
            .map(t => t.symbol)
            .filter(sym => typeof sym === 'string' && sym.toUpperCase().endsWith('_USDT'))
        );

        // Use symbol-based detection for USDT contracts
        const usdtContracts = contracts.filter(c =>
          typeof c.symbol === 'string' && c.symbol.toUpperCase().endsWith('_USDT')
        );

        // Count API-tradeable contracts for logging
        const apiTradeableContracts = usdtContracts.filter(c => c.apiAllowed === true);

        // Cross-reference: only include symbols present in tickers AND in contracts
        const contractSymbolSet = new Set(usdtContracts.map(c => c.symbol));
        const intersection = Array.from(tickerUsdtSet)
          .filter(sym => contractSymbolSet.has(sym))
          .map(sym => sym.replace('_', ''));

        const uniqueSorted = Array.from(new Set(intersection)).sort();

        console.log('📊 TradingView Export - Result:', {
          usdtPairs: uniqueSorted.length,
          apiTradeable: apiTradeableContracts.length,
          apiSymbols: apiTradeableContracts.map(c => c.symbol).join(', ') || 'None'
        });

        if (mounted) {
          setAllPairs(uniqueSorted);
          // Store contract data for filtering (map symbol -> apiAllowed)
          setContractsData(usdtContracts.map(c => ({
            symbol: c.symbol,
            apiAllowed: c.apiAllowed === true
          })));
        }
      } catch (e: any) {
        console.error('Failed to build TradingView export list', e);
        if (mounted) setError(e?.message || 'Failed to fetch contracts/tickers');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSymbols();
    return () => { mounted = false; };
  }, [isOpen]);

  const tradingViewLines = useMemo(() => tickers.map((s) => `MEXC:${s}${isPerpFormat ? '.P' : ''}`), [tickers, isPerpFormat]);
  const textContent = useMemo(() => tradingViewLines.join('\n'), [tradingViewLines]);

  // Calculate file count for split mode
  const fileCount = useMemo(() => {
    if (!splitMode) return 1;
    return Math.ceil(tradingViewLines.length / PAIRS_PER_FILE);
  }, [splitMode, tradingViewLines.length]);

  const handleDownload = async (type: 'txt' | 'csv') => {
    const date = new Date().toISOString().slice(0, 10);
    const formatTag = isPerpFormat ? 'perp' : 'futures';

    if (!splitMode) {
      // Single file download (existing behavior)
      const blob = new Blob([textContent + '\n'], {
        type: type === 'txt' ? 'text/plain;charset=utf-8' : 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mexc-usdt-${formatTag}-tradingview-${date}.${type}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } else {
      // Multi-file ZIP download
      await handleSplitDownload(type, date, formatTag);
    }
  };

  const handleSplitDownload = async (type: 'txt' | 'csv', date: string, formatTag: string) => {
    const zip = new JSZip();
    const parts: { partNumber: number; content: string; count: number }[] = [];

    // Split trading pairs into chunks
    for (let i = 0; i < tradingViewLines.length; i += PAIRS_PER_FILE) {
      const chunk = tradingViewLines.slice(i, i + PAIRS_PER_FILE);
      const partNumber = Math.floor(i / PAIRS_PER_FILE) + 1;
      const content = chunk.join('\n') + '\n';

      parts.push({
        partNumber,
        content,
        count: chunk.length
      });

      // Add file to ZIP
      const fileName = `mexc-usdt-${formatTag}-tradingview-${date}-part${partNumber}.${type}`;
      zip.file(fileName, content);
    }

    // Create summary file
    const summaryLines = [
      `MEXC USDT ${formatTag.toUpperCase()} TradingView Export Summary`,
      `Export Date: ${date}`,
      `Total Trading Pairs: ${tradingViewLines.length}`,
      `Files Generated: ${parts.length}`,
      `Pairs Per File: ${PAIRS_PER_FILE}`,
      `Format: ${isPerpFormat ? 'MEXC:SYMBOLUSDT.P' : 'MEXC:SYMBOLUSDT'}`,
      '',
      'File Details:',
      ...parts.map(p => `  - Part ${p.partNumber}: ${p.count} pairs (mexc-usdt-${formatTag}-tradingview-${date}-part${p.partNumber}.${type})`)
    ];
    zip.file(`_SUMMARY_${date}.txt`, summaryLines.join('\n') + '\n');

    // Generate and download ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mexc-usdt-${formatTag}-tradingview-${date}-split.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textContent + '\n');
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg sm:max-w-2xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Export for TradingView</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              USDT pairs formatted as {isPerpFormat ? 'MEXC:SYMBOLUSDT.P' : 'MEXC:SYMBOLUSDT'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* Info */}
          <div className="flex items-start space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
            <p>
              Use this to import all active MEXC USDT Perpetual pairs into a TradingView watchlist.
              Current format: <span className="font-mono">{`MEXC:SYMBOLUSDT${isPerpFormat ? '.P' : ''}`}</span> (one per line).
            </p>
          </div>
          {/* Controls Row: Toggles */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300" title="Some TradingView setups require .P for perpetual contracts; use this to build a separate watchlist.">
              <input
                type="checkbox"
                checked={isPerpFormat}
                onChange={(e) => setIsPerpFormat(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
              />
              <span>Export as Perpetual symbols (.P suffix)</span>
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300" title="Split export into multiple files with 700 pairs each for easier TradingView import">
              <input
                type="checkbox"
                checked={splitMode}
                onChange={(e) => setSplitMode(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
              />
              <span>Split into multiple files (700 pairs each)</span>
            </label>

            <label
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
              title="API-tradeable pairs can be used for automated trading via the MEXC API. Currently limited to select contracts."
            >
              <input
                type="checkbox"
                checked={apiTradeableOnly}
                onChange={(e) => setApiTradeableOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500"
              />
              <span>API-tradeable pairs only</span>
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                ({apiTradeableCount} available)
              </span>
            </label>

            {apiTradeableOnly && !loading && !error && (
              <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded">
                <Info className="w-4 h-4" />
                <span>Showing only contracts that support programmatic trading via MEXC API</span>
              </div>
            )}

            {splitMode && !loading && !error && (
              <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded">
                <Package className="w-4 h-4" />
                <span>Will generate {fileCount} file{fileCount > 1 ? 's' : ''} with up to {PAIRS_PER_FILE} pairs each</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!loading && !error && (
            <div className="flex items-center gap-2 justify-end">
              {splitMode ? (
                <>
                  <button
                    onClick={() => handleDownload('txt')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium"
                    title="Download all parts as ZIP (TXT format)"
                  >
                    <Package className="w-4 h-4" /> Download All Parts (TXT)
                  </button>
                  <button
                    onClick={() => handleDownload('csv')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium"
                    title="Download all parts as ZIP (CSV format)"
                  >
                    <Package className="w-4 h-4" /> Download All Parts (CSV)
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleDownload('txt')}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
                    title="Download as .txt"
                  >
                    <Download className="w-4 h-4" /> TXT
                  </button>
                  <button
                    onClick={() => handleDownload('csv')}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
                    title="Download as .csv"
                  >
                    <Download className="w-4 h-4" /> CSV
                  </button>
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
                    title="Copy to clipboard"
                  >
                    <Clipboard className="w-4 h-4" /> Copy
                  </button>
                </>
              )}
            </div>
          )}

          {/* Status */}
          {loading && (
            <div className="text-sm text-gray-600 dark:text-gray-300">Fetching current USDT perpetual pairs…</div>
          )}
          {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

          {/* Preview */}
          {!loading && !error && (
            <div>
              <div className="mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Pairs found: {tradingViewLines.length}
                  {splitMode && ` (${fileCount} file${fileCount > 1 ? 's' : ''} will be generated)`}
                </span>
              </div>
              {splitMode ? (
                <div className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-900">
                  <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                    <p className="font-semibold">Split Mode Preview:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      {Array.from({ length: fileCount }, (_, i) => {
                        const start = i * PAIRS_PER_FILE;
                        const end = Math.min(start + PAIRS_PER_FILE, tradingViewLines.length);
                        const count = end - start;
                        return (
                          <li key={i}>
                            Part {i + 1}: {count} pairs ({tradingViewLines[start]} ... {tradingViewLines[end - 1]})
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Click "Download All Parts" to get a ZIP file containing all parts plus a summary file.
                    </p>
                  </div>
                </div>
              ) : (
                <textarea
                  className="w-full h-64 font-mono text-xs p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200"
                  readOnly
                  value={textContent}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

